import { z } from 'zod';
import { PRODUCT_NAME } from './product';

/**
 * Subscriptions (ADR 0013): what an agency pays for the program each month and
 * the period invoices that carry it. The price rule and the period arithmetic live
 * here so the API, the billing job and the console's preview agree to the cent.
 */

export const BILLING_CURRENCIES = ['USD', 'EUR', 'TRY'] as const;
export type BillingCurrency = (typeof BILLING_CURRENCIES)[number];

export const BILLING_STATES = ['ok', 'due', 'overdue', 'read_only'] as const;
export type BillingState = (typeof BILLING_STATES)[number];

export const BILLING_PAYMENT_METHODS = ['transfer', 'card', 'cash', 'other'] as const;
export type BillingPaymentMethod = (typeof BILLING_PAYMENT_METHODS)[number];

/** Paid extras the platform turns on per agency. */
export const TENANT_FEATURES = ['bizimhesap'] as const;
export type TenantFeature = (typeof TENANT_FEATURES)[number];

export interface PriceRule {
  basePrice: string;
  includedCompanies: number;
  extraCompanyPrice: string;
  discount: string;
}

export interface ChargeLine {
  label: string;
  quantity: number;
  unitPrice: string;
  amount: string;
}

const cents = (v: string) => Math.round(Number(v) * 100);
const money = (c: number) => (c / 100).toFixed(2);

/**
 * One month's charge: the base price covers `includedCompanies` companies, each
 * further active company adds `extraCompanyPrice`, the discount comes off last and
 * the total never drops below zero.
 */
export function monthlyCharge(
  rule: PriceRule,
  companies: number,
): { lines: ChargeLine[]; amount: string } {
  const extra = Math.max(0, companies - rule.includedCompanies);
  const base = cents(rule.basePrice);
  const lines: ChargeLine[] = [
    {
      label: `${PRODUCT_NAME} aylık kullanım (${rule.includedCompanies} şirket dahil)`,
      quantity: 1,
      unitPrice: money(base),
      amount: money(base),
    },
  ];
  if (extra > 0) {
    lines.push({
      label: 'Ek şirket',
      quantity: extra,
      unitPrice: money(cents(rule.extraCompanyPrice)),
      amount: money(cents(rule.extraCompanyPrice) * extra),
    });
  }
  const gross = lines.reduce((t, l) => t + cents(l.amount), 0);
  const off = Math.min(gross, cents(rule.discount));
  if (off > 0) {
    lines.push({ label: 'İndirim', quantity: 1, unitPrice: money(-off), amount: money(-off) });
  }
  return { lines, amount: money(gross - off) };
}

// Periods ------------------------------------------------------------------------------------

const parts = (isoDay: string) => isoDay.split('-').map(Number) as [number, number, number];

/** "YYYY-MM-DD" plus whole months; subscription days stop at 28, so the day always exists. */
export function addMonths(isoDay: string, months: number): string {
  const [y, m, d] = parts(isoDay);
  return new Date(Date.UTC(y, m - 1 + months, d)).toISOString().slice(0, 10);
}

export function addDays(isoDay: string, days: number): string {
  const [y, m, d] = parts(isoDay);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * The period `today` falls in, for a subscription that started on `startsOn`;
 * null before it starts. A period runs from the start day to the day before the
 * same day next month.
 */
export function periodOn(startsOn: string, today: string): { start: string; end: string } | null {
  if (today < startsOn) return null;
  const [sy, sm] = parts(startsOn);
  const [ty, tm] = parts(today);
  let n = (ty - sy) * 12 + (tm - sm);
  if (addMonths(startsOn, n) > today) n -= 1;
  return { start: addMonths(startsOn, n), end: addDays(addMonths(startsOn, n + 1), -1) };
}

// Inputs -------------------------------------------------------------------------------------

const amount = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim().replace(',', '.'))
  .refine((v) => /^\d{1,9}(\.\d{1,2})?$/.test(v), 'Tutarı yazın, örn. 500 ya da 99,90')
  .transform((v) => Number(v).toFixed(2));
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarihi seçin');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null));

export const subscriptionInput = z.object({
  currency: z.enum(BILLING_CURRENCIES).default('USD'),
  basePrice: amount,
  includedCompanies: z.coerce.number().int().min(1, 'En az 1').max(99),
  extraCompanyPrice: amount,
  discount: amount.default('0'),
  startsOn: isoDay.refine(
    (v) => Number(v.slice(8, 10)) <= 28,
    'Ayın 28’inden sonraki günler her ay yok; 1 ile 28 arası bir gün seçin',
  ),
  dueDays: z.coerce.number().int().min(0).max(60).default(7),
  graceDays: z.coerce.number().int().min(0).max(90).default(10),
  notes: optionalText(2000),
});
export type SubscriptionInput = z.input<typeof subscriptionInput>;

export const markInvoicePaidInput = z.object({
  paidOn: isoDay,
  paymentMethod: z.enum(BILLING_PAYMENT_METHODS).default('transfer'),
  paymentRef: optionalText(120),
  note: optionalText(500),
});
export type MarkInvoicePaidInput = z.input<typeof markInvoicePaidInput>;

export const voidInvoiceInput = z.object({
  reason: z.string().trim().min(3, 'Neden iptal edildiğini yazın').max(500),
});

export const billingSettingsInput = z.object({
  payee: z.string().trim().min(1, 'Alıcı adını yazın').max(120),
  bankDetails: optionalText(2000),
  contactEmail: z
    .union([z.literal(''), z.email('Geçerli bir e-posta yazın')])
    .nullable()
    .optional()
    .transform((v) => (v ? v : null)),
  contactPhone: optionalText(40),
});
export type BillingSettingsInput = z.input<typeof billingSettingsInput>;

export const updatePlatformTenantInput = z.object({
  status: z.enum(['trial', 'active', 'suspended', 'closed']).optional(),
  trialEndsAt: isoDay.nullable().optional(),
  features: z.array(z.enum(TENANT_FEATURES)).optional(),
});
export type UpdatePlatformTenantInput = z.input<typeof updatePlatformTenantInput>;

// BizimHesap (ADR 0014) ----------------------------------------------------------------------

export const BH_TAX_RATES = [0, 1, 10, 20] as const;

export const connectBizimHesapInput = z.object({
  token: z.string().trim().min(8, 'BizimHesap API anahtarını yapıştırın').max(200),
  firmId: optionalText(200),
});
export type ConnectBizimHesapInput = z.input<typeof connectBizimHesapInput>;

export const bizimHesapInvoiceInput = z.object({
  date: isoDay,
  taxRate: z.coerce
    .number()
    .refine((v) => (BH_TAX_RATES as readonly number[]).includes(v), 'KDV oranını seçin'),
  currency: z.enum(['TRY', 'EUR', 'USD', 'GBP']),
  note: optionalText(1000),
  lines: z
    .array(
      z.object({
        productId: z.string().trim().max(100).nullable().optional(),
        productName: z.string().trim().min(1, 'Kalem adını yazın').max(200),
        note: z.string().trim().max(2000).default(''),
        unitPrice: amount.refine((v) => Number(v) > 0, 'Tutar sıfırdan büyük olmalı'),
      }),
    )
    .min(1, 'En az bir kalem ekleyin')
    .max(50),
});
export type BizimHesapInvoiceInput = z.input<typeof bizimHesapInvoiceInput>;
